/**
 * CustomVisualization — Implements the Visualization interface using a layer-based
 * composition model. DJs build presets by stacking effect layers; this class renders
 * them each frame by iterating through the layer stack.
 */

import { Visualization, FrameData, VisualizationSetting } from '../types';
import { BeatPulse, SmoothedValue } from '../utils/audioUtils';
import { CustomVisualizationPreset, CustomLayerDefinition, AudioSource } from './types';
import { LayerRenderer } from './layerRenderers/LayerRenderer';
import { createLayerRenderer } from './layerRenderers';

export class CustomVisualization implements Visualization {
  id: string;
  name: string;
  category = 'custom' as const;
  description: string;
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private preset: CustomVisualizationPreset;
  private layerRenderers: Map<string, LayerRenderer> = new Map();
  private smoothers: Map<string, SmoothedValue> = new Map();
  private beatPulse = new BeatPulse(0.93);

  constructor(preset: CustomVisualizationPreset) {
    this.preset = preset;
    this.id = `custom-${preset.id}`;
    this.name = preset.name;
    this.description = preset.description || 'Custom visualization';
  }

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;

    // Initialize a renderer for each layer
    for (const layer of this.preset.layers) {
      const renderer = createLayerRenderer(layer.type);
      renderer.init(canvas);
      this.layerRenderers.set(layer.id, renderer);
    }
  }

  render(frame: FrameData): void {
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    this.beatPulse.update(frame.isBeat, frame.beatIntensity);

    // Clear with trail
    const trail = this.preset.globalSettings.trailAmount;
    if (trail > 0) {
      ctx.fillStyle = `rgba(0, 0, 0, ${1 - trail})`;
      ctx.fillRect(0, 0, width, height);
    } else {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, width, height);
    }

    // Render each visible layer in order
    for (const layer of this.preset.layers) {
      if (!layer.visible) continue;
      const renderer = this.layerRenderers.get(layer.id);
      if (!renderer) continue;

      const resolved = this.resolveSettings(layer, frame);

      ctx.save();
      ctx.globalAlpha = layer.opacity;
      if (layer.blendMode === 'additive') {
        ctx.globalCompositeOperation = 'lighter';
      } else if (layer.blendMode === 'multiply') {
        ctx.globalCompositeOperation = 'multiply';
      }

      renderer.render(ctx, frame, resolved, width, height);

      ctx.restore();
    }

    // Global beat flash
    if (this.preset.globalSettings.beatFlash && this.beatPulse.value > 0.05) {
      const flashAlpha = this.beatPulse.value * this.preset.globalSettings.beatFlashIntensity * 0.1;
      ctx.fillStyle = `rgba(255, 255, 255, ${flashAlpha})`;
      ctx.fillRect(0, 0, width, height);
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
    for (const renderer of this.layerRenderers.values()) {
      renderer.resize(width, height);
    }
  }

  destroy(): void {
    for (const renderer of this.layerRenderers.values()) {
      renderer.destroy();
    }
    this.layerRenderers.clear();
    this.smoothers.clear();
  }

  getSettings(): VisualizationSetting[] {
    return [
      {
        key: 'trailAmount', label: 'Trail', type: 'range',
        value: this.preset.globalSettings.trailAmount, min: 0, max: 0.98, step: 0.02,
      },
      {
        key: 'beatFlashIntensity', label: 'Beat Flash', type: 'range',
        value: this.preset.globalSettings.beatFlashIntensity, min: 0, max: 1, step: 0.1,
      },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.trailAmount === 'number')
      this.preset.globalSettings.trailAmount = settings.trailAmount;
    if (typeof settings.beatFlashIntensity === 'number')
      this.preset.globalSettings.beatFlashIntensity = settings.beatFlashIntensity;
  }

  // ── Private helpers ───────────────────────────────────────────────────────

  /** Resolve layer settings by combining static values with audio reactivity */
  private resolveSettings(
    layer: CustomLayerDefinition,
    frame: FrameData,
  ): Record<string, number> {
    const resolved: Record<string, number> = {};

    for (const [key, baseValue] of Object.entries(layer.settings)) {
      if (typeof baseValue !== 'number') {
        resolved[key] = baseValue as unknown as number;
        continue;
      }

      const mapping = layer.reactivity[key];
      if (!mapping || mapping.source === 'none') {
        resolved[key] = baseValue;
        continue;
      }

      // Get the audio value (0-1)
      const audioValue = this.getAudioValue(frame, mapping.source);

      // Modulate: baseValue + (audioValue * intensity * baseValue)
      const modulated = baseValue + audioValue * mapping.intensity * baseValue;

      // Apply smoothing
      const smoothKey = `${layer.id}:${key}`;
      let smoother = this.smoothers.get(smoothKey);
      if (!smoother) {
        smoother = new SmoothedValue(mapping.smoothing);
        this.smoothers.set(smoothKey, smoother);
      }
      resolved[key] = smoother.update(modulated);
    }

    return resolved;
  }

  private getAudioValue(frame: FrameData, source: AudioSource): number {
    switch (source) {
      case 'bassEnergy': return frame.bassEnergy;
      case 'midEnergy': return frame.midEnergy;
      case 'trebleEnergy': return frame.trebleEnergy;
      case 'overallEnergy': return frame.overallEnergy;
      case 'beatIntensity': return frame.beatIntensity;
      default: return 0;
    }
  }
}
